import {ErrorCodeType, RubyPlayErrorsHandler} from '@gongxigames/framework/integrations/rubyplay/RubyPlayErrorsHandler';
import {
    consoleLogger,
    CoreDI,
    inject,
    IEventManagerWithAsync,
    ISettings,
    LanguageService,
    injectable,
    GameClientWrapperSettings,
    Message,
    messages,
    ModuleIsEnabledFilter
} from '@gongxigames/framework/slot';
import {ILightAndWonderMessageChannel} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderInterface';
import {
    LAWError,
    LAWGameServerError,
    RubyErrorCodeLAWErrorCodes,
    RubyErrorCodeToLAWCategory
} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderType';
import {LightAndWonderIntegrationDI} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderIntegrationDI';
import {isEnabledLightAndWonder} from '@gongxigames/framework/integrations/lightAndWonder/isEnabledLightAndWonder';

const LW_FREEROUNDS_INVALID_BET_LEVEL = 'freeRoundsInvalidBetErrorLW';

@injectable()
export class LightAndWonderErrorsHandler extends RubyPlayErrorsHandler {
    public static readonly NAME = LightAndWonderIntegrationDI.ErrorsHandler;
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledLightAndWonder;

    constructor(
        @inject(CoreDI.IEventManagerWithAsync) private eventManager: IEventManagerWithAsync,
        @inject(LightAndWonderIntegrationDI.MessageChannel) private messageChannel: ILightAndWonderMessageChannel,
        @inject(CoreDI.LanguageService) protected languageService: LanguageService,
        @inject(CoreDI.Settings) protected _settings: ISettings
    ) {
        super(eventManager);

        this.setupOnEvents();
    }

    public handleGameServerError(error: LAWGameServerError): void {
        this._gameServerError = error;
        const code = this._gameServerError?.data?.code ?? '';
        const errorMessage = this.getMessageError();
        const integrationsPayload = this._gameServerError?.data?.integrationsPayload;

        if (integrationsPayload) {
            this.handleLightAndWonderError(integrationsPayload);
        } else if (errorMessage) {
            this.handleErrorsWithGCM(code, errorMessage);
        }

        consoleLogger.error(error);
    }

    protected setupOnEvents(): void {
        this.on('lnw.clientInsufficientFunds', this.handleClientInsufficientFundsError);
        this.on('lnw.clientLostConnection', this.handleClientLostConnectionError);
    }

    private on(id: string | symbol, listener: Function, priority?: number): void {
        this.eventManager.on(id, listener.bind(this, id), this, priority);
    }

    protected getMessageError(): Message {
        const DEFAULT_ERROR = {code: '', message: ''};
        const {code, message} = this._gameServerError ? this._gameServerError.data : DEFAULT_ERROR;
        const errorMessage = this.getMessageByCode(code as ErrorCodeType);

        if (this.isCustomError(code)) {
            const errorData = this.getCustomErrorData(message);

            if (errorMessage.message === '' && errorData.message) {
                errorMessage.message = errorData.message;
            }
        } else if (this.isCustomServerError(code)) {
            errorMessage.message = message;
        }

        if (errorMessage?.message === '') {
            errorMessage.message = message;
        }

        return errorMessage || messages.INTERNAL_ERROR;
    }

    public handleInitSessionError(error: any): void {
        const code = error.errorCode as ErrorCodeType;
        const errorMessage = this.getMessageByCode(code);
        const {integrationsPayload} = error;

        if (integrationsPayload) {
            this.handleLightAndWonderError(integrationsPayload);
        } else if (errorMessage) {
            this.handleErrorsWithGCM(code, errorMessage);
        } else {
            this.internalErrorMessage(messages.INTERNAL_ERROR);
        }

        consoleLogger.error(error);
    }

    public handleRequestError(xhr: XMLHttpRequest, isTimeout: boolean): void {
        const code = xhr?.response?.data?.code as ErrorCodeType;
        const errorMessage = this.getMessageByCode(xhr?.response?.data?.code as ErrorCodeType);
        const integrationsPayload = xhr?.response?.data?.integrationsPayload;

        if (integrationsPayload) {
            this.handleLightAndWonderError(integrationsPayload);
        } else if (errorMessage) {
            this.handleErrorsWithGCM(code, errorMessage);
        } else {
            if (isTimeout) {
                this.internalErrorMessage(messages.CONNECTION_TIMEOUT);
            } else {
                this.internalErrorMessage(messages.CONNECTION_ERROR);
            }
        }

        consoleLogger.error(xhr);
    }

    public handleSlowConnection(): void {
        this.messageChannel.gameReadyMessage();
        this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleError', {
            errorCode: '0',
            category: 'CONNECTION_ERROR',
            message: this.getMessageText(messages.SLOW_CONNECTION),
            severity: 'ERROR'
        });
        this.hideSplashScreen();
    }

    protected internalErrorMessage(errorMessage: Message): void {
        this.messageChannel.gameReadyMessage();
        this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleError', {
            errorCode: '1',
            category: 'NON_RECOVERABLE_ERROR',
            message: this.getMessageText(errorMessage),
            severity: 'ERROR'
        });
        this.hideSplashScreen();
    }

    protected getMessageText(errorMessage: Message): string {
        const message = errorMessage.message ?? '';

        return typeof message === 'string' ? message : this.getErrorMessageText(message.id);
    }

    private getErrorMessageText(id: string): string {
        return this.languageService.get(id);
    }

    protected handleLightAndWonderError(integrationsPayload: string): void {
        try {
            const error: LAWError = JSON.parse(integrationsPayload);
            const {errorCategory, operatorErrorCode, message} = error;

            this.messageChannel.gameReadyMessage();
            this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleError', {
                errorCode: operatorErrorCode,
                category: errorCategory,
                message,
                severity: 'ERROR'
            });
        } catch (e) {
            consoleLogger.error('Could not parse the error text');
            consoleLogger.error(e);
        }
    }
    protected handleErrorsWithGCM(code: ErrorCodeType, errorMessage: Message): void {
        this.messageChannel.gameReadyMessage();
        this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleError', {
            errorCode: RubyErrorCodeLAWErrorCodes[code] ?? 'CRITICAL',
            category: RubyErrorCodeToLAWCategory[code] ?? '1',
            message: this.getMessageText(errorMessage),
            severity: 'ERROR'
        });

        this.hideSplashScreen();
        this.sendQuitToLobby();
    }

    protected hideSplashScreen(): void {
        this.getEventManager().emit('General.Game.HIDE_SPLASH_SCREEN');
    }

    protected handleClientInsufficientFundsError(): void {
        this.messageChannel.gameReadyMessage();
        this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleError', {
            errorCode: '1006',
            category: 'INSUFFICIENT_FUNDS',
            message: this.getMessageText(messages.LOW_BALANCE),
            severity: 'INFO'
        });
        this.hideSplashScreen();
    }

    public handleFreeRoundsInvalidBetLevelError(): void {
        this.messageChannel.gameReadyMessage();
        this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleError', {
            errorCode: 'FREEROUNDS_INVALID_BET_LEVEL',
            category: 'NON_RECOVERABLE_ERROR',
            message: this.languageService.get(LW_FREEROUNDS_INVALID_BET_LEVEL),
            severity: 'ERROR'
        });
    }

    protected handleClientLostConnectionError(): void {
        this.messageChannel.gameReadyMessage();
        this.messageChannel.sendMsgToGCM('com.lnw.gcm.handleError', {
            errorCode: '0',
            category: 'CONNECTION_ERROR',
            message: this.getMessageText(messages.LOST_CONNECTION),
            severity: 'ERROR'
        });
    }

    protected sendQuitToLobby(): void {
        this.messageChannel.sendMsgToGCM('com.lnw.gcm.redirect', {
            url: (this._settings as GameClientWrapperSettings).get('homeUrl')!
        });
    }
}
